import { COLOR_TOKENS, ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, ChartData, ChartOptions } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

const FIGMA_CHART_DIAMETER = 120;
const FIGMA_MULTI_CHART_DIAMETER = 240;

ChartJS.register(ArcElement, Tooltip);

// Multi-segment type
export type DonutSegment = Readonly<{
  label: string;
  value: number;
  color: ColorToken;
}>;

export type DonutChartProps = Readonly<{
  variant:
    | {
        type: 'solo';
        percentageFilled: number;
      }
    | {
        type: 'multi';
        segments: DonutSegment[];
      };
  size?: 'xsmall' | 'small' | 'medium' | 'large' | number;
  isTooltipEnabled?: boolean;
  /** Radius of the hole, as a chart.js cutout value. Pass 0 for a filled pie. */
  cutout?: number | string;
}>;

const defaultOptions = (
  isTooltipEnabled: boolean,
  cutout: number | string,
): ChartOptions<'doughnut'> => ({
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 1,
  cutout,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: isTooltipEnabled,
      callbacks: {
        label: (context) => {
          const label = context.label || '';
          const value = context.parsed as number;

          return `${label}: ${value}%`;
        },
      },
    },
  },
  rotation: (-90 * Math.PI) / 180,
});

const getChartSize = (size: 'xsmall' | 'small' | 'medium' | 'large' | number) => {
  if (typeof size === 'number') return size;

  switch (size) {
    case 'xsmall':
      return 80;
    case 'small':
      return FIGMA_CHART_DIAMETER;
    case 'medium':
      return 180;
    case 'large':
      return FIGMA_MULTI_CHART_DIAMETER;
    default:
      return 240;
  }
};

export const DonutChart = ({
  variant,
  size = 'small',
  isTooltipEnabled = true,
  cutout = '50%',
}: DonutChartProps) => {
  if (variant.type === 'solo') {
    const safeValue = Math.min(100, Math.max(0, variant.percentageFilled));

    // Zero-width segments still draw a radial border seam, so drop them entirely.
    const segments = [
      { value: safeValue, color: COLOR_TOKENS['Colors/Border/border-brand'] },
      { value: 100 - safeValue, color: COLOR_TOKENS['Colors/Border/border-secondary'] },
    ].filter((segment) => segment.value > 0);
    const segmentColors = segments.map((segment) => segment.color);

    const chartData: ChartData<'doughnut'> = {
      datasets: [
        {
          data: segments.map((segment) => segment.value),
          backgroundColor: segmentColors,
          borderColor: COLOR_TOKENS['Colors/Base/Black'],
          borderWidth: 1,
          borderRadius: 0,
          // if the tooltip is disabled, we need to set the hover background color to the same as the background color
          ...(!isTooltipEnabled && {
            hoverBackgroundColor: segmentColors,
          }),
        },
      ],
    };

    const chartSize = getChartSize(size);

    return (
      <Box sx={{ width: `${chartSize}px`, height: `${chartSize}px` }}>
        <Doughnut data={chartData} options={defaultOptions(isTooltipEnabled, cutout)} />
      </Box>
    );
  }

  // Multi variant logic
  const safeSegments = variant.segments.map((s) => ({
    ...s,
    value: Math.max(0, Math.min(100, s.value)),
  }));

  const total = safeSegments.reduce((sum, seg) => sum + seg.value, 0);
  const chartSegments = [...safeSegments];

  if (total < 100) {
    chartSegments.push({
      label: 'Remaining',
      value: 100 - total,
      color: 'Colors/Border/border-secondary',
    });
  }

  const chartData: ChartData<'doughnut'> = {
    labels: chartSegments.map((s) => s.label),
    datasets: [
      {
        data: chartSegments.map((s) => s.value),
        backgroundColor: chartSegments.map((s) => COLOR_TOKENS[s.color]),
        borderColor: COLOR_TOKENS['Colors/Base/Black'],
        borderWidth: 1,
        borderRadius: 0,
        ...(!isTooltipEnabled && {
          hoverBackgroundColor: chartSegments.map((s) => COLOR_TOKENS[s.color]),
        }),
      },
    ],
  };

  const chartSize = getChartSize(size);

  return (
    <Box sx={{ width: `${chartSize}px`, height: `${chartSize}px` }}>
      <Doughnut data={chartData} options={defaultOptions(isTooltipEnabled, cutout)} />
    </Box>
  );
};
